
/**
 * Module dependencies.
 */

import { QueryObserverBaseResult, useQuery } from 'react-query';
import { fetchProductApplications } from './fetch-product-applications';
import { useMemo } from 'react';
import { useRouter } from 'next/router';

/**
 * `Props` type.
 */

type Props<P> = QueryObserverBaseResult<P, unknown>;

/**
 * `Options` type.
 */

type Options<P> = {
  initialData?: P | null,
  staleTime?: number
};

/**
 * Default stale time.
 *
 * Applications are reference data that barely changes. Without a default here
 * every consumer — footer, filters, search, navbar — refetches the same list
 * on mount and on every window focus.
 */

const defaultStaleTime = 30 * 60 * 1000;

/**
 * Action type.
 */

const actionType = ({ locale }): string => {
  return `${locale}-product-applications`;
};

/**
 * `useProductApplications` hook.
 */

function useProductApplications<P>(options?: Options<P>): Props<P> {
  const { locale } = useRouter();
  const variables = useMemo(() => ({ locale }), [locale]);
  const result = useQuery([actionType(variables), variables], () => {
    return fetchProductApplications(variables);
  }, {
    initialData: options?.initialData,
    staleTime: options?.staleTime ?? defaultStaleTime
  });

  return result;
}

/**
 * Export `useProductApplications` hook.
 */

export default useProductApplications;
